import { createSlice } from '@reduxjs/toolkit';
import type { Filter, FilterState, Filters } from '@/utilities/ProductGalleryUtils';
import { FILTERSWITHSELECTION, parseGalleryFilters } from '@/utilities/ProductGalleryUtils';
import { POPULAR_BRANDS, POPULAR_BRANDS_LESS } from '@/constants/Brand';

const prodInitialState = {
  id: 0,
  name: '',
  brand: { id: '', name: '', logo: '' },
  color: {
    id: '',
    code: '',
    rank: 0,
  },
  flashEligible: false,
  category: '',
  imgUrl: '',
  tags: [],
  rating: 0,
  styleCode: '',
  budgetTag: '',
  variants: [],
};

const filtersInitialState: FilterState = {
  types: [],
  brands: [],
  style: [],
  madeFor: [],
  price: [],
  season: [],
  color: [],
  tags: [],
  colorHex: '',
  options: JSON.parse(JSON.stringify(FILTERSWITHSELECTION)),
  openGallery: false,
  searchText: '',
  filterSearchParams: '',
  selectedProduct: prodInitialState,
  pageNum: 0,
  showMobileFilters: false,
  mobileFiltersApplied: false,
};

export const galleryFiltersSlice = createSlice({
  name: 'filters',
  initialState: filtersInitialState,
  reducers: {
    setFilter: (state, action) => {
      const { filterType, value } = action.payload;
      const filterValue = state[filterType as keyof FilterState];

      if (Array.isArray(filterValue)) {
        const index = filterValue.indexOf(value.includes('-') ? value.split[1] : value);

        if (index === -1) {
          filterValue.push(value);
          state.tags.push(`${filterType}-${value}`);
        } else {
          const tagIndex = state.tags.indexOf(`${filterType}-${value}`);
          if (tagIndex !== -1) {
            state.tags.splice(tagIndex, 1);
          }

          filterValue.splice(index, 1);

          if (filterType === 'color') {
            state.color = [];
            state.colorHex = '';
          }
        }
      }

      if (!state.mobileFiltersApplied) {
        state.filterSearchParams = parseGalleryFilters(state);
      }
      state.pageNum = 0;
    },
    setFilterOptions: (state, action) => {
      const { filterType, option } = action.payload;

      const filterOptions = filterType
        ? state.options[filterType as keyof Filters]
        : Object.values(state.options).flat();

      if (filterType !== 'color' && filterType !== 'brands') {
        const filterOption = filterOptions.find((item: Filter) => item.id === option);

        if (filterOption) {
          filterOption.isSelected = !filterOption.isSelected;
        }
      } else if (filterType === 'brands') {
        const allBrandsFilterOption = state.options.allBrands.find(
          (item: Filter) => item.id === option
        );

        if (allBrandsFilterOption) {
          allBrandsFilterOption.isSelected = !allBrandsFilterOption.isSelected;
        }
        const brandsFilterOption = state.options.brands.find((item: Filter) => item.id === option);

        if (brandsFilterOption) {
          brandsFilterOption.isSelected = !brandsFilterOption.isSelected;
        }
      }

      if (!state.mobileFiltersApplied) {
        state.filterSearchParams = parseGalleryFilters(state);
      }
      state.pageNum = 0;
    },
    setColorOptions: (state, action) => {
      const { isColorCode, value } = action.payload;

      if (isColorCode) {
        state.colorHex = value;
      } else {
        state.color = [];
        state.color.push(value);
      }

      if (!state.mobileFiltersApplied) {
        state.filterSearchParams = parseGalleryFilters(state);
      }
      state.pageNum = 0;
    },
    openGallery: (state, action) => {
      state.openGallery = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
      state.openGallery = false;
    },
    setSearchTerm: (state, action) => {
      state.searchText = action.payload;

      state.filterSearchParams = parseGalleryFilters(state);
      state.pageNum = 0;
    },
    setPageNum: (state, action) => {
      state.pageNum = action.payload;
    },
    setShowMobileFilters: (state, action) => {
      state.showMobileFilters = action.payload;

      if (state.showMobileFilters) {
        state.mobileFiltersApplied = true;
      }
    },
    applyMobileFilters: (state, action) => {
      state.filterSearchParams = parseGalleryFilters(state);
      state.mobileFiltersApplied = action.payload;

      state.showMobileFilters = false;
    },
    cancelMobileFilters: (state, action) => {
      state.showMobileFilters = action.payload;

      if (
        state.types.length ||
        state.brands.length ||
        state.style.length ||
        state.madeFor.length ||
        state.price.length ||
        state.season.length ||
        state.color.length ||
        state.colorHex
      ) {
        /** Reset the filters when cacelled */
        state.options = JSON.parse(JSON.stringify(FILTERSWITHSELECTION));
        state.types = [];
        state.brands = [];
        state.style = [];
        state.madeFor = [];
        state.price = [];
        state.season = [];
        state.color = [];
        state.tags = [];
        state.colorHex = '';
        /** The change in params triggers the products API call */
        state.filterSearchParams = parseGalleryFilters(state);
      }
    },
    resetFilters: () => filtersInitialState,
    setBrandsVisibilityInStore: (state, action) => {
      if (action.payload === 'Less') {
        state.options.brands = state.options.allBrands.filter((value: Filter) =>
          POPULAR_BRANDS_LESS.includes(value.name)
        );
      } else {
        state.options.brands = state.options.allBrands.filter((value: Filter) =>
          POPULAR_BRANDS.includes(value.name)
        );
      }
    },
    filterBrands: (state, action) => {
      if (action.payload) {
        state.options.brands = state.options.allBrands.filter((value: Filter) =>
          value.name.toLowerCase().includes(action.payload.toLowerCase())
        );
      } else {
        state.options.brands = state.options.allBrands.filter((value: Filter) =>
          POPULAR_BRANDS_LESS.includes(value.name)
        );
      }
    },
  },
});

/** Action creators are generated for each case reducer function */
export const {
  setFilter,
  resetFilters,
  setFilterOptions,
  setColorOptions,
  openGallery,
  setSelectedProduct,
  setSearchTerm,
  setPageNum,
  setShowMobileFilters,
  applyMobileFilters,
  cancelMobileFilters,
  setBrandsVisibilityInStore,
  filterBrands,
} = galleryFiltersSlice.actions;

export default galleryFiltersSlice.reducer;
